import { useState, useEffect, useCallback } from 'react';
import { Send, Mail, Reply, UserX, TrendingUp, Activity, Loader2, BarChart3, PlayCircle, CheckCircle2, AlertCircle, SkipForward } from 'lucide-react';
import PageHeader from '../../components/PageHeader';

interface StatsData {
  daily_limit: number;
  sent_today: number;
  remaining_today: number;
  sent_this_week: number;
  total_prospects: number;
  status_breakdown: Record<string, number>;
  in_review: number;
  approved_queued: number;
  total_sent: number;
  reply_rate: number;
  isla_breakdown: Array<{ isla: string; cnt: number }>;
  seq_breakdown: Array<{ sequence_type: string; cnt: number }>;
  config: {
    warmup_week: number;
    daily_limit_override: number | null;
    is_paused: number;
    total_sent: number;
    total_replied: number;
    total_unsubscribed: number;
  };
}

function StatCard({ icon: Icon, label, value, sub, color = 'teal' }: {
  icon: typeof Send;
  label: string;
  value: number | string;
  sub?: string;
  color?: 'teal' | 'green' | 'blue' | 'orange' | 'red';
}) {
  const colors = {
    teal: 'bg-teal-50 text-teal-600',
    green: 'bg-green-50 text-green-600',
    blue: 'bg-blue-50 text-blue-600',
    orange: 'bg-orange-50 text-orange-600',
    red: 'bg-red-50 text-red-600',
  };
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <div className="flex items-center gap-3 mb-3">
        <div className={`p-2 rounded-lg ${colors[color]}`}>
          <Icon size={18} />
        </div>
        <span className="text-sm text-gray-500">{label}</span>
      </div>
      <p className="text-2xl font-bold text-gray-900">{value}</p>
      {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
    </div>
  );
}

type SendResult =
  | { sent: true; to: string; subject: string; step: number; prospect_id: number }
  | { skipped: true; reason: string }
  | { error: string };

export default function OutreachDashboardPage() {
  const [stats, setStats] = useState<StatsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState<SendResult | null>(null);

  const loadStats = useCallback(() => {
    return fetch('/api/outreach/stats')
      .then(r => r.json())
      .then(d => setStats(d as StatsData));
  }, []);

  useEffect(() => {
    loadStats().finally(() => setLoading(false));
  }, [loadStats]);

  const handleSendNext = async () => {
    setSending(true);
    setSendResult(null);
    try {
      const r = await fetch('/api/outreach/process-queue', { method: 'POST' });
      const data = await r.json() as SendResult;
      setSendResult(data);
      // Refresh stats after send
      await loadStats();
    } catch (e) {
      setSendResult({ error: (e as Error).message });
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 size={24} className="animate-spin text-teal-500" />
      </div>
    );
  }

  if (!stats) return null;

  const sb = stats.status_breakdown;
  const replied = (sb['replied'] ?? 0) + (sb['interested'] ?? 0) + (sb['meeting_booked'] ?? 0) + (sb['closed'] ?? 0);
  const inProgress = (sb['sent_step1'] ?? 0) + (sb['pending_step2'] ?? 0) + (sb['sent_step2'] ?? 0) + (sb['pending_step3'] ?? 0) + (sb['sent_step3'] ?? 0);

  return (
    <div className="max-w-5xl mx-auto">
      <PageHeader
        type="outreach"
        icon={BarChart3}
        title="Dashboard Outreach"
        subtitle={stats.config.is_paused ? '⏸ Campaña pausada · Modo manual' : '▶ Campaña activa · Modo manual'}
      />

      {/* Manual send button */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 mb-6 flex items-center gap-4 flex-wrap">
        <div className="flex-1">
          <p className="text-sm font-semibold text-gray-800">Envío manual</p>
          <p className="text-xs text-gray-400 mt-0.5">
            {stats.approved_queued > 0
              ? `${stats.approved_queued} email${stats.approved_queued !== 1 ? 's' : ''} aprobado${stats.approved_queued !== 1 ? 's' : ''} en cola · ${stats.remaining_today} restantes hoy`
              : 'No hay emails aprobados en cola'}
          </p>
        </div>

        {sendResult && (
          <div className="text-sm flex items-center gap-2">
            {'sent' in sendResult && sendResult.sent && (
              <span className="flex items-center gap-1.5 text-green-700 bg-green-50 px-3 py-1.5 rounded-lg">
                <CheckCircle2 size={14} />
                Enviado a <strong>{sendResult.to}</strong> · Paso {sendResult.step}
              </span>
            )}
            {'skipped' in sendResult && sendResult.skipped && (
              <span className="flex items-center gap-1.5 text-orange-700 bg-orange-50 px-3 py-1.5 rounded-lg">
                <SkipForward size={14} />
                Saltado: {sendResult.reason.replace(/_/g, ' ')}
              </span>
            )}
            {'error' in sendResult && (
              <span className="flex items-center gap-1.5 text-red-700 bg-red-50 px-3 py-1.5 rounded-lg">
                <AlertCircle size={14} />
                {sendResult.error}
              </span>
            )}
          </div>
        )}

        <button
          onClick={handleSendNext}
          disabled={sending || stats.config.is_paused === 1 || stats.approved_queued === 0 || stats.remaining_today === 0}
          className="flex items-center gap-2 px-4 py-2.5 bg-teal-600 text-white text-sm font-medium rounded-lg hover:bg-teal-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          {sending ? <Loader2 size={16} className="animate-spin" /> : <PlayCircle size={16} />}
          {sending ? 'Enviando...' : 'Enviar siguiente'}
        </button>
      </div>

      {/* Warmup progress */}
      <div className="bg-teal-900 text-white rounded-xl p-5 mb-6">
        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="text-teal-300 text-sm">Semana de calentamiento</p>
            <p className="text-2xl font-bold">{stats.config.warmup_week} / 8</p>
          </div>
          <div className="text-right">
            <p className="text-teal-300 text-sm">Límite diario</p>
            <p className="text-2xl font-bold">{stats.daily_limit} emails</p>
          </div>
        </div>
        <div className="h-2 bg-teal-800 rounded-full overflow-hidden">
          <div
            className="h-full bg-teal-400 rounded-full transition-all"
            style={{ width: `${Math.min(100, (stats.config.warmup_week / 8) * 100)}%` }}
          />
        </div>
        <div className="flex justify-between text-xs text-teal-400 mt-1">
          <span>Wk1: 5/d</span>
          <span>Wk2: 10/d</span>
          <span>Wk3: 20/d</span>
          <span>Wk4: 30/d</span>
          <span>Wk5+: 50/d</span>
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-6">
        <StatCard icon={Send} label="Enviados hoy" value={stats.sent_today}
          sub={`${stats.remaining_today} restantes hoy`} color="teal" />
        <StatCard icon={Mail} label="En cola (aprobados)" value={stats.approved_queued}
          sub={`${stats.in_review} en revisión`} color="blue" />
        <StatCard icon={Activity} label="Total enviados" value={stats.total_sent}
          sub={`${inProgress} en seguimiento`} color="green" />
        <StatCard icon={Reply} label="Tasa respuesta" value={`${stats.reply_rate}%`}
          sub={`${replied} respuestas totales`} color="orange" />
        <StatCard icon={TrendingUp} label="Enviados semana" value={stats.sent_this_week}
          color="teal" />
        <StatCard icon={UserX} label="Bajas" value={stats.config.total_unsubscribed}
          color="red" />
        <StatCard icon={Mail} label="Total prospects" value={stats.total_prospects}
          sub={`${sb['pending'] ?? 0} pendientes`} color="blue" />
        <StatCard icon={Reply} label="Meetings" value={sb['meeting_booked'] ?? 0}
          sub={`${sb['interested'] ?? 0} interesados`} color="green" />
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Status breakdown */}
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h3 className="font-semibold text-gray-900 mb-4">Estado de prospects</h3>
          <div className="space-y-2">
            {Object.entries(sb)
              .sort((a, b) => b[1] - a[1])
              .map(([status, cnt]) => (
                <div key={status} className="flex items-center gap-2">
                  <span className="text-sm text-gray-600 flex-1 truncate">{status}</span>
                  <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-teal-400 rounded-full"
                      style={{ width: `${Math.round((cnt / stats.total_prospects) * 100)}%` }}
                    />
                  </div>
                  <span className="text-sm font-medium text-gray-700 w-10 text-right">{cnt}</span>
                </div>
              ))}
          </div>
        </div>

        {/* By isla */}
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h3 className="font-semibold text-gray-900 mb-4">Por isla</h3>
          <div className="space-y-2">
            {stats.isla_breakdown.map(row => (
              <div key={row.isla} className="flex items-center gap-2">
                <span className="text-sm text-gray-600 flex-1 truncate">{row.isla}</span>
                <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-blue-400 rounded-full"
                    style={{ width: `${Math.round((row.cnt / stats.total_prospects) * 100)}%` }}
                  />
                </div>
                <span className="text-sm font-medium text-gray-700 w-10 text-right">{row.cnt}</span>
              </div>
            ))}
          </div>

          <h3 className="font-semibold text-gray-900 mt-6 mb-3">Por secuencia</h3>
          <div className="flex gap-3">
            {stats.seq_breakdown.map(row => (
              <div key={row.sequence_type} className="flex-1 bg-gray-50 rounded-lg p-3 text-center">
                <p className="text-lg font-bold text-gray-900">{row.cnt}</p>
                <p className="text-xs text-gray-500">Seq {row.sequence_type}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
